import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Eraser, Redo2, Undo2 } from "lucide-react";
import { Button } from "../../ui/kit";
import type { BoardProps } from "../types";
import { specFor } from "../catalog";
import { SKY, collinearGroups, isSolved, linesUsed, snapPoint, visited, type Pt, type StarsSpec } from "./geometry";

const round1 = (v: number): number => Math.round(v * 10) / 10;

function sanitize(saved: unknown): Pt[] {
  const t = (saved as { trail?: unknown } | null)?.trail;
  if (!Array.isArray(t)) return [];
  const out: Pt[] = [];
  for (const p of t.slice(0, 40)) {
    const x = Number((p as Pt)?.x);
    const y = Number((p as Pt)?.y);
    if (Number.isFinite(x) && Number.isFinite(y)) out.push({ x: Math.max(0, Math.min(SKY, x)), y: Math.max(0, Math.min(SKY, y)) });
  }
  return out;
}

export default function StarsBoard({ id, saved, hintLevel, showAnswer, restartToken, onState, onSolved }: BoardProps) {
  const spec = (specFor(id) as { spec: StarsSpec }).spec;
  const [trail, setTrail] = useState<Pt[]>(() => sanitize(saved));
  const trailRef = useRef(trail);
  trailRef.current = trail;
  const past = useRef<Pt[][]>([]);
  const future = useRef<Pt[][]>([]);
  const [, bump] = useState(0);
  const [aim, setAim] = useState<Pt | null>(null);
  const pressing = useRef(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const solvedRef = useRef<boolean | null>(null);
  if (solvedRef.current === null) solvedRef.current = isSolved(spec, trail);

  const seen = useMemo(() => visited(spec.stars, trail, spec.tol), [spec, trail]);
  const used = linesUsed(trail);
  const solved = isSolved(spec, trail);
  const groups = useMemo(() => (spec.kind === "constellation" ? collinearGroups(spec.stars, spec.tol) : []), [spec]);

  // restart
  const lastToken = useRef(restartToken);
  useEffect(() => {
    if (lastToken.current === restartToken) return;
    lastToken.current = restartToken;
    past.current = [];
    future.current = [];
    solvedRef.current = false;
    setTrail([]);
    setAim(null);
    onState({ trail: [] });
  }, [restartToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = useCallback((next: Pt[]) => {
    setTrail(next);
    onState({ trail: next });
  }, [onState]);

  useEffect(() => {
    if (solved && !solvedRef.current) {
      solvedRef.current = true;
      onSolved(`${used} line${used === 1 ? "" : "s"}`);
    }
    if (!solved) solvedRef.current = false;
  }, [solved, used, onSolved]);

  // ---- keyboard: a pen you steer with the arrow keys ----
  const [kbd, setKbd] = useState<Pt | null>(null);
  const [kbdNote, setKbdNote] = useState("");
  const describeSpot = (p: Pt): string => {
    const near = spec.stars.findIndex((st) => Math.hypot(st.x - p.x, st.y - p.y) <= spec.starSnap);
    return near >= 0 ? `On star ${near + 1} of ${spec.stars.length}.` : `Pen at ${Math.round(p.x)} across, ${Math.round(p.y)} down.`;
  };
  const onKey = (e: React.KeyboardEvent<SVGSVGElement>) => {
    const cur = kbd ?? trailRef.current[trailRef.current.length - 1] ?? { x: 50, y: 50 };
    const step = e.shiftKey ? 0.5 : e.ctrlKey || e.metaKey ? 10 : 2;
    let next: Pt | null = null;
    if (e.key === "ArrowRight") next = { x: cur.x + step, y: cur.y };
    else if (e.key === "ArrowLeft") next = { x: cur.x - step, y: cur.y };
    else if (e.key === "ArrowUp") next = { x: cur.x, y: cur.y - step };
    else if (e.key === "ArrowDown") next = { x: cur.x, y: cur.y + step };
    else if (e.key.toLowerCase() === "n" || e.key.toLowerCase() === "p") {
      // jump from star to star, in the order they were drawn on the sky (left to right)
      const sorted = spec.stars.map((st, i) => ({ st, i })).sort((a, b) => a.st.x - b.st.x || a.st.y - b.st.y);
      const at = sorted.findIndex((o) => Math.hypot(o.st.x - cur.x, o.st.y - cur.y) < 0.6);
      const dir = e.key.toLowerCase() === "n" ? 1 : -1;
      const pick = sorted[(at + dir + sorted.length) % sorted.length] ?? sorted[0];
      next = { x: pick.st.x, y: pick.st.y };
    } else if (e.key === "Enter" || e.key === " ") {
      const p = snapPoint(spec, kbd ?? cur);
      const last = trailRef.current[trailRef.current.length - 1];
      if (!last || Math.hypot(p.x - last.x, p.y - last.y) > 1.2) {
        past.current.push(trailRef.current);
        future.current = [];
        commit([...trailRef.current, { x: round1(p.x), y: round1(p.y) }]);
        bump((n) => n + 1);
        setKbdNote(`Corner placed. ${describeSpot(p)}`);
      }
      e.preventDefault();
      return;
    } else if (e.key === "Backspace" || e.key === "Delete") {
      undo();
      setKbdNote("Undid the last corner.");
      e.preventDefault();
      return;
    } else return;
    e.preventDefault();
    const clamped = { x: Math.max(0, Math.min(SKY, next.x)), y: Math.max(0, Math.min(SKY, next.y)) };
    const snapped = snapPoint(spec, clamped);
    setKbd(clamped);
    setAim(snapped);
    setKbdNote(describeSpot(snapped));
  };

  const toPoint = (e: React.PointerEvent): Pt => {
    const svg = svgRef.current!;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const p = pt.matrixTransform(svg.getScreenCTM()!.inverse());
    return snapPoint(spec, { x: Math.max(0, Math.min(SKY, p.x)), y: Math.max(0, Math.min(SKY, p.y)) });
  };

  const onDown = (e: React.PointerEvent<SVGSVGElement>) => {
    if (e.button !== 0 && e.pointerType === "mouse") return;
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    pressing.current = true;
    setAim(toPoint(e));
    e.preventDefault();
  };
  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (pressing.current || e.pointerType === "mouse") setAim(toPoint(e));
  };
  const onUp = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!pressing.current) return;
    pressing.current = false;
    const p = toPoint(e);
    const cur = trailRef.current;
    const last = cur[cur.length - 1];
    if (!last || Math.hypot(p.x - last.x, p.y - last.y) > 1.2) {
      past.current.push(cur);
      future.current = [];
      commit([...cur, { x: round1(p.x), y: round1(p.y) }]);
      bump((n) => n + 1);
    }
    if (e.pointerType !== "mouse") setAim(null);
  };

  const undo = () => {
    const prev = past.current.pop();
    if (!prev) return;
    future.current.push(trailRef.current);
    commit(prev);
    bump((n) => n + 1);
  };
  const redo = () => {
    const nxt = future.current.pop();
    if (!nxt) return;
    past.current.push(trailRef.current);
    commit(nxt);
    bump((n) => n + 1);
  };
  const clear = () => {
    if (!trailRef.current.length) return;
    past.current.push(trailRef.current);
    future.current = [];
    commit([]);
    bump((n) => n + 1);
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // the line that would be drawn if the pointer were released now, and the stars it would light
  const last = trail[trail.length - 1];
  const preview = last && aim ? [last, aim] : null;
  const previewSeen = preview ? visited(spec.stars, preview, spec.tol) : [];
  const visitedCount = seen.filter(Boolean).length;
  const path = trail.map((p, i) => `${i ? "L" : "M"}${p.x} ${p.y}`).join(" ");

  // the "Pebble noticed" hint on constellations: one group of stars that line up
  const hintGroup = hintLevel >= 2 && groups.length ? groups[0] : null;
  const hintLine = (() => {
    if (!hintGroup) return null;
    const pts = hintGroup.map((i) => spec.stars[i]).sort((a, b) => a.x - b.x || a.y - b.y);
    const a = pts[0];
    const b = pts[pts.length - 1];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    const ext = 9;
    return { x1: a.x - (dx / len) * ext, y1: a.y - (dy / len) * ext, x2: b.x + (dx / len) * ext, y2: b.y + (dy / len) * ext };
  })();

  const status = solved
    ? `All ${spec.stars.length} stars in ${used} line${used === 1 ? "" : "s"}!`
    : trail.length === 0
      ? "Tap or click to put down your pen, then keep tapping to draw straight lines."
      : used > spec.lines
        ? `That's ${used} lines and the limit is ${spec.lines}. Undo a few or clear and rethink.`
        : visitedCount === spec.stars.length
          ? `Every star visited, but with ${used} lines. The limit is ${spec.lines}.`
          : `${visitedCount} of ${spec.stars.length} stars visited.`;

  return (
    <div className="board stars-board">
      <div className={"sky-card" + (solved ? " is-solved" : "")}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SKY} ${SKY}`}
          className="stars-svg"
          role="application"
          tabIndex={0}
          aria-roledescription="star trail board"
          aria-label={`${spec.title}. Keyboard: arrow keys steer the pen, Shift for small steps, Control for big steps, N and P jump between stars, Enter puts down a corner, Backspace undoes.`}
          onKeyDown={onKey}
          onFocus={() => { if (!kbd) { const start = trailRef.current[trailRef.current.length - 1] ?? { x: 50, y: 50 }; setKbd(start); setAim(snapPoint(spec, start)); } }}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={() => { pressing.current = false; setAim(null); }}
          onPointerLeave={() => !pressing.current && setAim(null)}
          style={{ touchAction: "none" }}
        >
          <defs>
            <radialGradient id="starglow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#fff6c8" stopOpacity="0.9" />
              <stop offset="45%" stopColor="#f3c84f" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#f3c84f" stopOpacity="0" />
            </radialGradient>
          </defs>

          {hintLine && <line {...hintLine} className="hint-line" />}
          {showAnswer && (
            <g className="answer-trail">
              <path d={spec.solution.map((p, i) => `${i ? "L" : "M"}${p.x} ${p.y}`).join(" ")} />
              {spec.solution.map((p, i) => (
                <g key={i}>
                  <circle cx={p.x} cy={p.y} r={2.1} />
                  <text x={p.x} y={p.y} textAnchor="middle" dominantBaseline="central">{i + 1}</text>
                </g>
              ))}
            </g>
          )}

          {path && <path d={path} className="trail" />}
          {preview && <line x1={preview[0].x} y1={preview[0].y} x2={preview[1].x} y2={preview[1].y} className="trail-preview" />}

          {spec.stars.map((s, i) => {
            const lit = seen[i];
            const would = previewSeen[i];
            return (
              <g key={i} className={"star" + (lit ? " lit" : "") + (would && !lit ? " would" : "")}>
                <circle cx={s.x} cy={s.y} r={5} fill="url(#starglow)" className="star-glow" />
                <circle cx={s.x} cy={s.y} r={1.9} className="star-core" />
                {(lit || would) && <circle cx={s.x} cy={s.y} r={3.5} className="star-ring" />}
              </g>
            );
          })}

          {trail.map((p, i) => (
            <circle key={i} cx={p.x} cy={p.y} r={i === trail.length - 1 ? 1.5 : 1.05} className={"corner" + (i === trail.length - 1 ? " end" : "")} />
          ))}
          {aim && <circle cx={aim.x} cy={aim.y} r={1.3} className="aim" />}
        </svg>
      </div>

      <div className="stars-meter" aria-hidden="true">
        <span className="meter-label">Lines</span>
        <span className="pips">
          {Array.from({ length: Math.max(spec.lines, used) }, (_, i) => (
            <span key={i} className={"pip" + (i < used ? " on" : "") + (i >= spec.lines ? " over" : "")} />
          ))}
        </span>
        <span className="meter-label">Stars {visitedCount}/{spec.stars.length}</span>
      </div>
      <p className={"board-status" + (solved ? " good" : "")} aria-live="polite">{status}</p>
      <p className="sr-only" aria-live="assertive">{kbdNote}</p>
      <p className="kbd-hint">Keyboard: Tab to the sky, arrows steer the pen (Shift = small steps), N and P jump between stars, Enter puts down a corner.</p>
      <div className="board-tools">
        <Button size="small" onClick={undo} disabled={!past.current.length} aria-label="Undo"><Undo2 size={17} /> Undo</Button>
        <Button size="small" onClick={redo} disabled={!future.current.length} aria-label="Redo"><Redo2 size={17} /> Redo</Button>
        <Button size="small" onClick={clear} disabled={!trail.length}><Eraser size={17} /> Start over</Button>
      </div>
    </div>
  );
}
